import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static values = { collectionId: String }
  static targets = ["scenarioRow", "personaModal", "personaForm", "personaSearch", "personaList"]
  
  clearMetricsTimestamps = new Map() // Track when each scenario was cleared

  connect() {
    this.subscription = consumer.subscriptions.create(
      { 
        channel: "ScenarioCollectionChannel", 
        id: this.collectionIdValue 
      },
      {
        connected: () => {
          console.log("Connected to ScenarioCollectionChannel")
        },

        disconnected: () => {
          console.log("Disconnected from ScenarioCollectionChannel")
        },

        received: (data) => {
          console.log("Received update:", data)
          this.handleUpdate(data)
        }
      }
    )
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  handleUpdate(data) {
    if (data.type === 'scenario_update') {
      this.updateScenarioRow(data.scenario)
    } else if (data.type === 'conversation_update') {
      // Update the specific scenario row based on conversation data
      this.updateScenarioFromConversation(data.conversation)
      // Also update success history if needed
      this.updateSuccessHistory(data.conversation)
    } else if (data.type === 'clear_metrics') {
      // Clear metrics for a scenario that's about to be run
      this.clearScenarioMetrics(data.scenario_id)
      // Add blinking indicator to success history
      this.addPendingToSuccessHistory(data.scenario_id)
    }
  }

  updateScenarioRow(scenario) {
    const row = this.findScenarioRow(scenario.id)
    if (!row) return

    // Update status cell
    const statusCell = row.querySelector('[data-status-cell]')
    if (statusCell && scenario.last_conversation) {
      statusCell.innerHTML = this.renderStatus(scenario.last_conversation)
    }

    // Update other cells as needed
    const successCell = row.querySelector('[data-success-cell]')
    if (successCell && scenario.last_conversation) {
      successCell.innerHTML = this.renderIntentSuccess(scenario.last_conversation)
    }

    const scoreCell = row.querySelector('[data-score-cell]')
    if (scoreCell && scenario.last_conversation) {
      scoreCell.innerHTML = this.renderAvgScore(scenario.last_conversation)
    }

    const durationCell = row.querySelector('[data-duration-cell]')
    if (durationCell && scenario.last_conversation) {
      durationCell.innerHTML = this.renderDuration(scenario.last_conversation)
    }
  }

  updateScenarioFromConversation(conversation) {
    console.log('Updating scenario from conversation:', conversation)
    
    const row = this.findScenarioRow(conversation.scenario_id)
    if (!row) {
      console.log('No row found for scenario:', conversation.scenario_id)
      return
    }
    
    // Check if this update is from before the scenario was cleared
    const clearTimestamp = this.clearMetricsTimestamps.get(conversation.scenario_id)
    if (clearTimestamp) {
      const conversationStartTime = new Date(conversation.started_at).getTime()
      if (conversationStartTime < clearTimestamp) {
        console.log('Ignoring update from old conversation that started before clear')
        return
      }
      // Remove the timestamp since we've now received a valid update
      this.clearMetricsTimestamps.delete(conversation.scenario_id)
    }

    // Update the row's click handler with the new conversation URL
    if (conversation.id) {
      row.dataset.scenarioCollectionConversationUrlParam = `/admin/conversations/${conversation.id}`
      
      // If the row doesn't have the click action yet, add it
      if (!row.dataset.action || !row.dataset.action.includes('click->scenario-collection#navigateToConversation')) {
        row.dataset.action = 'click->scenario-collection#navigateToConversation'
        row.classList.add('cursor-pointer')
      }
      
      // Remove the opacity that was added when clearing metrics
      row.classList.remove('opacity-75')
    }

    // Update status
    const statusCell = row.querySelector('[data-status-cell]')
    if (statusCell) {
      statusCell.innerHTML = this.renderStatus(conversation)
    }

    // Update intent progress for all statuses
    const successCell = row.querySelector('[data-success-cell]')
    if (successCell) {
      console.log('Updating intent progress - passed:', conversation.intents_passed, 'failed:', conversation.intents_failed, 'total:', conversation.intents_total)
      successCell.innerHTML = this.renderIntentSuccess(conversation)
    }

    // Update score and duration only for completed conversations
    if (['passed', 'failed', 'errored'].includes(conversation.status)) {
      const scoreCell = row.querySelector('[data-score-cell]')
      if (scoreCell) {
        scoreCell.innerHTML = this.renderAvgScore(conversation)
      }

      const durationCell = row.querySelector('[data-duration-cell]')
      if (durationCell) {
        durationCell.innerHTML = this.renderDuration(conversation)
      }
    }
  }

  findScenarioRow(scenarioId) {
    return this.element.querySelector(`[data-scenario-id="${scenarioId}"]`)
  }

  renderStatus(conversation) {
    const statusBadges = {
      passed: '<span class="badge badge-green">Passed</span>',
      failed: '<span class="badge badge-red">Failed</span>',
      errored: '<span class="badge badge-red">Errored</span>',
      running: '<span class="badge badge-orange">Running</span>',
      pending: '<span class="badge badge-gray">Pending</span>'
    }

    const badge = statusBadges[conversation.status] || '<span class="text-xs text-gray-400">Unknown</span>'
    const timestamp = conversation.finished_at || conversation.started_at
    const timeStr = timestamp ? new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }) : 'In progress'

    return `
      <div>
        ${badge}
        <div class="text-xs text-gray-500 mt-1">${timeStr}</div>
      </div>
    `
  }

  renderIntentSuccess(conversation) {
    const passed = conversation.intents_passed || 0
    const failed = conversation.intents_failed || 0
    const total = conversation.intents_total || 0
    const completed = passed + failed

    // If we don't have any data, show placeholder
    if (total === 0 && conversation.intents_total === undefined) {
      return '<span class="text-gray-400">-</span>'
    }

    const passedPercentage = total > 0 ? Math.round((passed / total) * 100) : 0
    const failedPercentage = total > 0 ? Math.round((failed / total) * 100) : 0

    return `
      <div class="flex items-center">
        <div class="flex bg-gray-200 rounded-full h-2 mr-3 overflow-hidden" style="width: 100px;">
          ${passed > 0 ? `<div class="bg-green-500 h-2" style="width: ${passedPercentage}%"></div>` : ''}
          ${failed > 0 ? `<div class="bg-red-500 h-2" style="width: ${failedPercentage}%"></div>` : ''}
        </div>
        <span class="text-sm text-gray-600">
          ${completed}/${total}
        </span>
      </div>
    `
  }

  renderAvgScore(conversation) {
    if (!['passed', 'failed', 'errored'].includes(conversation.status)) {
      return '<span class="text-gray-400">-</span>'
    }

    const avgScore = conversation.avg_score
    if (avgScore === null || avgScore === undefined) {
      return '<span class="text-gray-400">-</span>'
    }

    const score = Math.round(avgScore * 100 * 10) / 10
    const colorClass = score >= 70 ? 'text-green-600' : 'text-amber-600'

    return `
      <span class="font-semibold ${colorClass}">
        ${score}%
      </span>
    `
  }

  renderDuration(conversation) {
    if (!conversation.started_at || !conversation.finished_at) {
      return '<span class="text-gray-400">-</span>'
    }

    const start = new Date(conversation.started_at)
    const end = new Date(conversation.finished_at)
    const duration = (end - start) / 1000 // in seconds

    if (duration < 60) {
      return `${Math.round(duration)}s`
    } else {
      return `${Math.round(duration / 60 * 10) / 10}m`
    }
  }

  clearScenarioMetrics(scenarioId) {
    const row = this.findScenarioRow(scenarioId)
    if (!row) return
    
    console.log('Clearing metrics for scenario:', scenarioId)
    
    // Record when this scenario was cleared
    this.clearMetricsTimestamps.set(scenarioId, Date.now())

    // Remove click handler and URL since we're queued
    delete row.dataset.scenarioCollectionConversationUrlParam
    row.classList.remove('cursor-pointer')
    row.classList.add('opacity-75')

    // Clear status to show pending with link not available
    const statusCell = row.querySelector('[data-status-cell]')
    if (statusCell) {
      statusCell.innerHTML = `
        <div>
          <span class="badge badge-gray">Pending</span>
          <div class="text-xs text-gray-500 mt-1">Queued</div>
          <div class="text-xs text-gray-400 mt-1 italic">Link not available</div>
        </div>
      `
    }

    // Clear intent success - this is important!
    const successCell = row.querySelector('[data-success-cell]')
    if (successCell) {
      successCell.innerHTML = '<span class="text-gray-400">-</span>'
    }

    // Clear average score
    const scoreCell = row.querySelector('[data-score-cell]')
    if (scoreCell) {
      scoreCell.innerHTML = '<span class="text-gray-400">-</span>'
    }

    // Clear duration
    const durationCell = row.querySelector('[data-duration-cell]')
    if (durationCell) {
      durationCell.innerHTML = '<span class="text-gray-400">-</span>'
    }
  }

  addPendingToSuccessHistory(scenarioId) {
    // Find success history cell in the scenarios table
    const historyCell = document.querySelector(`[data-success-history-cell="${scenarioId}"]`)
    if (!historyCell) return

    const container = historyCell.querySelector('.flex')
    if (!container) return

    // Add a new blinking blue bar at the end
    const newBar = document.createElement('div')
    newBar.className = 'w-2 h-8 bg-blue-500 rounded-sm animate-pulse'
    newBar.title = 'Running'
    newBar.dataset.pending = 'true'
    
    // Remove "No runs" text if present
    const noRunsText = container.querySelector('.text-gray-400')
    if (noRunsText) {
      noRunsText.remove()
    }
    
    container.appendChild(newBar)
  }

  updateSuccessHistory(conversation) {
    // Find success history cell in the scenarios table
    const historyCell = document.querySelector(`[data-success-history-cell="${conversation.scenario_id}"]`)
    if (!historyCell) return

    const container = historyCell.querySelector('.flex')
    if (!container) return

    // Find the pending bar for this scenario
    const pendingBar = container.querySelector('[data-pending="true"]')
    if (!pendingBar) return

    // Update based on conversation status
    if (conversation.status === 'running') {
      // Keep it blinking blue
      pendingBar.className = 'w-2 h-8 bg-blue-500 rounded-sm animate-pulse'
      pendingBar.title = 'Running'
    } else if (conversation.status === 'passed') {
      // Turn it green
      pendingBar.className = 'w-2 h-8 bg-green-500 rounded-sm'
      pendingBar.title = `Passed - ${new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`
      pendingBar.dataset.pending = 'false'
    } else if (conversation.status === 'failed' || conversation.status === 'errored') {
      // Turn it red
      pendingBar.className = 'w-2 h-8 bg-red-500 rounded-sm'
      pendingBar.title = `${conversation.status.charAt(0).toUpperCase() + conversation.status.slice(1)} - ${new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`
      pendingBar.dataset.pending = 'false'
    }
  }

  navigateToConversation(event) {
    // Don't navigate if clicking on a link or button
    if (event.target.tagName === 'A' || event.target.tagName === 'BUTTON') {
      return
    }
    
    // Also check if the clicked element is inside a link or button
    if (event.target.closest('a') || event.target.closest('button')) {
      return
    }

    const url = event.currentTarget.dataset.scenarioCollectionConversationUrlParam
    if (url) {
      window.location.href = url
    }
  }

  openPersonaModal(event) {
    event.preventDefault()
    console.log('Opening persona modal...')
    console.log('Has modal target:', this.hasPersonaModalTarget)
    if (this.hasPersonaModalTarget) {
      console.log('Showing modal')
      this.personaModalTarget.classList.remove('hidden')
    } else {
      console.error('Modal target not found')
    }
  }

  closePersonaModal(event) {
    event.preventDefault()
    if (this.hasPersonaModalTarget) {
      this.personaModalTarget.classList.add('hidden')
    }
  }

  runWithPersona(event) {
    event.preventDefault()
    if (this.hasPersonaFormTarget) {
      this.personaFormTarget.submit()
    }
  }

  filterPersonas(event) {
    const searchTerm = event.target.value.toLowerCase()
    
    if (!this.hasPersonaListTarget) return
    
    const personaOptions = this.personaListTarget.querySelectorAll('[data-persona-option]')
    
    personaOptions.forEach(option => {
      const name = option.dataset.personaName || ''
      const description = option.dataset.personaDescription || ''
      
      if (searchTerm === '' || name.includes(searchTerm) || description.includes(searchTerm)) {
        option.style.display = 'flex'
      } else {
        option.style.display = 'none'
      }
    })
  }

  toggleAccordion(event) {
    event.preventDefault()
    const button = event.currentTarget
    const section = button.dataset.accordionSection
    const content = this.element.querySelector(`[data-accordion-content="${section}"]`)
    const icon = this.element.querySelector(`[data-accordion-icon="${section}"]`)
    
    if (!content || !icon) return
    
    const isExpanded = !content.classList.contains('hidden')
    
    if (isExpanded) {
      // Collapse
      content.classList.add('hidden')
      icon.classList.remove('rotate-180')
    } else {
      // Expand
      content.classList.remove('hidden')
      icon.classList.add('rotate-180')
    }
  }
}